import {
  Box,
  HStack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Link,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Divider,
  VStack,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";

import { AlgoliaContactObject } from "common/interfaces/Algolia";
import { dineroFormatCurrency } from "src/utils";
import { Link as RouterLink } from "react-router-dom";
import { KYDScope } from "@common/interfaces/ICognito";
import ShowIfHasScope from "src/Manager/ShowIfHasScope";

type ColumnKey = keyof AlgoliaContactObject | "name";
type ColumnType = "currency" | "string" | "date" | undefined;

interface Column {
  key: ColumnKey;
  label: string;
  type?: ColumnType;
}

export const FansTable = ({
  fans,
  onChangeSort,
  handleDelete,
  permissions,
  ...props
}: {
  fans: AlgoliaContactObject[];
  onChangeSort: (sortBy: string, sortOrder: string) => void;
  handleDelete: (contact: AlgoliaContactObject) => void;
  permissions: KYDScope[];
}) => {
  const columns: Column[] = [
    { key: "name", label: "Name" },
    { key: "total_attendance", label: "Attended" },
    { key: "total_spent", label: "Spend", type: "currency" },
    { key: "order_count", label: "Orders" },
    { key: "created_at", label: "Created On" },
  ];

  return (
    <Box
      w="100%"
      overflowX="auto"
      borderTop="1px solid"
      borderBottom="1px solid"
      borderColor="gray.200"
    >
      <Table {...props} variant="simple" w="100%">
        <Thead>
          <Tr height="40px">
            {columns.map((col) => (
              <Th
                key={col.key}
                textTransform="uppercase"
                fontSize={"small"}
                fontWeight="700"
                letterSpacing="0.5px"
                color="gray.700"
                pl={0}
                cursor="pointer"
              >
                <Text whiteSpace="nowrap" fontSize={"small"}>
                  {col.label}
                </Text>
              </Th>
            ))}
            <Th
              textTransform="uppercase"
              fontSize={"small"}
              fontWeight="700"
              letterSpacing="0.5px"
              color="gray.700"
              pl={0}
            >
              -
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {fans.map((fan) => {
            const link = `/campaigns/contacts/${fan.id}`;
            return (
              <Tr key={fan.objectID} _hover={{ bg: "gray.50" }}>
                {columns.map((col) => {
                  return (
                    <Td pl={0} key={col.key}>
                      <Link
                        as={RouterLink}
                        to={link}
                        _hover={{ textDecoration: "none" }}
                      >
                        {col.type === "currency" &&
                        typeof fan[col.key] === "object" &&
                        fan[col.key] !== null &&
                        "amount" in fan[col.key] ? (
                          dineroFormatCurrency(
                            Math.round((fan[col.key] as any)?.amount || 0)
                          )
                        ) : col.key === "created_at" ? (
                          new Date(fan.created_at).toLocaleDateString()
                        ) : col.key === "name" ? (
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm" fontWeight="medium">
                              {`${fan.first_name || ""} ${fan.last_name || ""}`}
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {fan.email || ""}
                            </Text>
                          </VStack>
                        ) : (
                          (fan[col.key] as React.ReactNode)
                        )}
                      </Link>
                    </Td>
                  );
                })}
                <Td pl={0}>
                  <ShowIfHasScope
                    permissions={permissions}
                    checkFor={["crm/contacts:write", "crm/write"]}
                  >
                    <Menu>
                      <MenuButton
                        as={Button}
                        variant="ghost"
                        size="sm"
                        borderRadius="6px"
                        border="1px solid #A0AEC0"
                        bg="#FFF"
                      >
                        <ChevronDownIcon color="#718096" width="16px" />
                      </MenuButton>
                      <MenuList minW="0" p={"12px"} fontSize={"13px"}>
                        <MenuItem as={RouterLink} to={link}>
                          View Contact
                        </MenuItem>
                        {!fan.user_id ? (
                          <>
                            <Divider borderColor={"#00000020"} my={2} />
                            <MenuItem onClick={() => handleDelete(fan)}>
                              Delete Contact
                            </MenuItem>
                          </>
                        ) : null}
                      </MenuList>
                    </Menu>
                  </ShowIfHasScope>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
};
